import ProjectFiles from '@/components/features/Projects/components/ProjectFiles';
import { AddressMapThumbnail } from '@/components/global/AddressMapThumbnail';
import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import { CalculationRequestSelect, type CalculationRequestOption } from '@/components/global/selectors/CalculationRequestSelect';
import { UnifiedStatusSelector } from '@/components/global/selectors/UnifiedStatusSelector';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { getCountryName } from '@/lib/country-utils';
import { cn, formatCurrency, formatDateForDisplay, formatDateTime } from '@/lib/utils';
import type { Project } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Activity, Building2, ClipboardList, Euro, Globe, House, Link2, ListChecks, Mail, MapPin, Tag, UserRound, Wrench } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

function buildProjectDisplayAddress(project: Project): string {
    const trimmed = project.address?.trim();
    if (trimmed) {
        return trimmed;
    }
    const street = project.address_street;
    const houseNumber = project.address_house_number;
    if (street || houseNumber) {
        return [street, houseNumber].filter(Boolean).join(' ');
    }
    const city = project.address_city;
    const postal = project.address_postal_code;
    const municipality = project.address_municipality;
    const parts = [city, postal, municipality].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : '';
}

/** A labelled value in the detail cards. Falls back to a muted placeholder when empty. */
function DetailField({ label, icon, fallback, children }: { label: string; icon?: ReactNode; fallback?: string; children?: ReactNode }) {
    return (
        <div className="space-y-1">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                {icon}
                {label}
            </div>
            <div className="text-sm font-medium break-words">{children ?? <span className="text-muted-foreground font-normal">{fallback}</span>}</div>
        </div>
    );
}

/**
 * One segment of the headline metrics strip: coloured icon, label, value.
 * `valueClassName` lets text values (company, owner) step down a size and wrap,
 * so a long company name stays inside its segment instead of pushing the row.
 */
function MetricTile({
    icon,
    tone,
    label,
    valueClassName,
    children,
}: {
    icon: ReactNode;
    tone: string;
    label: string;
    valueClassName?: string;
    children: ReactNode;
}) {
    return (
        <div className="flex items-start gap-3 p-4">
            <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
            <div className="min-w-0 flex-1">
                <div className="text-muted-foreground text-xs font-medium">{label}</div>
                <div className={cn('mt-0.5 text-lg font-semibold break-words', valueClassName)}>{children}</div>
            </div>
        </div>
    );
}

/** A compact tile inside the sidebar statistics card — subtle, never a card of its own. */
function StatTile({ icon, label, value, onClick }: { icon: ReactNode; label: string; value: ReactNode; onClick?: () => void }) {
    const body = (
        <>
            {/* The sidebar column is narrow, so long labels wrap rather than truncate. */}
            <div className="text-muted-foreground flex items-start gap-1.5 text-xs font-medium">
                <span className="mt-0.5 shrink-0">{icon}</span>
                <span className="min-w-0">{label}</span>
            </div>
            <div className="mt-1 text-sm font-semibold break-words">{value}</div>
        </>
    );

    if (!onClick) {
        return <div className="bg-muted/40 rounded-lg border p-3">{body}</div>;
    }

    return (
        <Button
            type="button"
            variant="ghost"
            onClick={onClick}
            className="bg-muted/40 h-auto w-full flex-col items-stretch justify-start rounded-lg border p-3 text-left whitespace-normal"
        >
            {body}
        </Button>
    );
}

type ProjectDetailsPanelProps = {
    project: Project;
    /** Lets the statistics tiles jump to the tab that owns the underlying records. */
    onNavigateToTab?: (tab: string) => void;
    /** The calculation request this project was priced from, if any. */
    linkedCalculationRequest?: CalculationRequestOption | null;
    /** The pick list — deferred, so it is absent on first paint. */
    calculationRequestOptions?: CalculationRequestOption[];
    canLinkCalculationRequest?: boolean;
    /** Contacts registered against this project's company. */
    contactCandidates?: PersonOption[];
    /** Everyone active, with the commercially-responsible roles surfaced first. */
    ownerCandidates?: { people: PersonOption[]; suggested_ids: string[] };
    /** Active colleagues holding the Project Manager role — a closed list, unlike the owner's. */
    projectManagerCandidates?: PersonOption[];
    /** Active colleagues holding the Engineer role — closed on the same terms. */
    projectEngineerCandidates?: PersonOption[];
    /**
     * Narrower than `canEditPeople`: only the project's owner, its creator or an
     * administrator decides who works it. The fields still render for everyone else,
     * read-only, so the assignments stay visible.
     */
    canAssignProjectStaff?: boolean;
    canEditPeople?: boolean;
    /** Holds `projects.status.change`. Without it the tile still shows the status, read-only. */
    canChangeStatus?: boolean;
};

export default function ProjectDetailsPanel({
    project,
    onNavigateToTab,
    linkedCalculationRequest = null,
    calculationRequestOptions = [],
    canLinkCalculationRequest = false,
    contactCandidates = [],
    ownerCandidates = { people: [], suggested_ids: [] },
    projectManagerCandidates = [],
    projectEngineerCandidates = [],
    canAssignProjectStaff = false,
    canEditPeople = false,
    canChangeStatus = false,
}: ProjectDetailsPanelProps) {
    const { settings } = useSettings();
    const { t } = useTranslations();

    const displayAddress = useMemo(() => buildProjectDisplayAddress(project), [project]);

    /**
     * The linked request is merged into the candidate list regardless of what the
     * (deferred) pick list carries, so the current value is always representable —
     * and therefore clearable — even before the options arrive.
     */
    const calculationRequestCandidates = useMemo(() => {
        const byId = new Map<string, CalculationRequestOption>();

        calculationRequestOptions.forEach((request) => byId.set(request.id, request));
        if (linkedCalculationRequest) {
            byId.set(linkedCalculationRequest.id, linkedCalculationRequest);
        }

        return Array.from(byId.values()).map((request) => ({
            ...request,
            note:
                request.project_id && request.project_id !== project.id
                    ? t('projects.show.details.calculation_request_other_project', 'Attached to another project')
                    : null,
        }));
    }, [calculationRequestOptions, linkedCalculationRequest, project.id, t]);

    /**
     * Whoever is set stays representable even if they have since fallen out of the
     * candidate set — a contact moved to another company, a suspended owner —
     * otherwise the picker would render an empty value over real data.
     */
    const contactPeople = useMemo(() => {
        const byId = new Map(contactCandidates.map((person) => [person.id, person]));
        // Added only when missing: the candidate entry carries the position badge,
        // which the record's own relation does not.
        if (project.contact && !byId.has(project.contact.id)) {
            byId.set(project.contact.id, {
                id: project.contact.id,
                first_name: project.contact.first_name,
                last_name: project.contact.last_name,
            });
        }

        return Array.from(byId.values());
    }, [contactCandidates, project.contact]);

    const ownerPeople = useMemo(() => {
        const byId = new Map(ownerCandidates.people.map((person) => [person.id, person]));
        // Added only when missing, so the candidate entry keeps its role badge.
        if (project.owner && !byId.has(project.owner.id)) {
            byId.set(project.owner.id, {
                id: project.owner.id,
                first_name: project.owner.first_name,
                last_name: project.owner.last_name,
            });
        }

        return Array.from(byId.values());
    }, [ownerCandidates.people, project.owner]);

    /**
     * Someone whose Project Manager role was revoked after they were assigned is no
     * longer a candidate, but must still render — otherwise the field would look
     * unassigned while the project is in fact still theirs.
     */
    const projectManagerPeople = useMemo(() => {
        const byId = new Map(projectManagerCandidates.map((person) => [person.id, person]));
        if (project.project_manager && !byId.has(project.project_manager.id)) {
            byId.set(project.project_manager.id, {
                id: project.project_manager.id,
                first_name: project.project_manager.first_name,
                last_name: project.project_manager.last_name,
            });
        }

        return Array.from(byId.values());
    }, [projectManagerCandidates, project.project_manager]);

    /** Same reasoning as projectManagerPeople, for a revoked Engineer role. */
    const projectEngineerPeople = useMemo(() => {
        const byId = new Map(projectEngineerCandidates.map((person) => [person.id, person]));
        if (project.project_engineer && !byId.has(project.project_engineer.id)) {
            byId.set(project.project_engineer.id, {
                id: project.project_engineer.id,
                first_name: project.project_engineer.first_name,
                last_name: project.project_engineer.last_name,
            });
        }

        return Array.from(byId.values());
    }, [projectEngineerCandidates, project.project_engineer]);

    /*
     * None of these fire a success toast of their own. Every endpoint below flashes
     * one, and useFlashToasts (mounted once in app-layout) turns that into the toast
     * — adding a client-side one here showed the user two.
     *
     * The error handlers stay: a refused assignment now comes back as a 422 with the
     * message on its field, so it lands here rather than in onSuccess.
     */
    const updateOwner = (userId: string | null) => {
        router.patch(
            route('projects.update-owner', project.id),
            { project_owner_id: userId },
            {
                preserveScroll: true,
                onError: (errors) => toast.error(errors.project_owner_id ?? t('projects.flash.owner_failed', 'Failed to update the project owner.')),
            },
        );
    };

    const updateProjectManager = (userId: string | null) => {
        router.patch(
            route('projects.update-project-manager', project.id),
            { project_manager_id: userId },
            {
                preserveScroll: true,
                onError: (errors) =>
                    toast.error(errors.project_manager_id ?? t('projects.flash.project_manager_failed', 'Failed to update the project manager.')),
            },
        );
    };

    const updateProjectEngineer = (userId: string | null) => {
        router.patch(
            route('projects.update-project-engineer', project.id),
            { project_engineer_id: userId },
            {
                preserveScroll: true,
                onError: (errors) =>
                    toast.error(errors.project_engineer_id ?? t('projects.flash.project_engineer_failed', 'Failed to update the engineer.')),
            },
        );
    };

    const updateContact = (personId: string | null) => {
        router.patch(
            route('projects.update-contact', project.id),
            { contact_id: personId },
            {
                preserveScroll: true,
                onError: (errors) => toast.error(errors.contact_id ?? t('projects.flash.contact_failed', 'Failed to update the contact person.')),
            },
        );
    };

    const linkCalculationRequest = (id: string | null) => {
        router.patch(
            route('projects.calculation-request.sync', project.id),
            { calculation_request_id: id },
            {
                preserveScroll: true,
                // Reload the project so the engineer seat the link may have filled
                // (see CalculationRequestService::setProject) shows without a refresh.
                onSuccess: () => router.reload({ only: ['project'] }),
                onError: () => toast.error(t('projects.flash.calculation_request_failed', 'Failed to update the calculation request.')),
            },
        );
    };

    const notSpecified = t('projects.sidebar.not_specified', 'Not specified');
    const countryLabel = project.company?.country ? getCountryName(project.company.country, settings.countries) || project.company.country : null;
    const hasLocation = Boolean(displayAddress.trim() || countryLabel || (project.address_lat != null && project.address_lng != null));
    // contract_number already carries the "/{manufacturing number}" suffix, so this
    // matches the page heading: "26P075/0 - Avikta UAB test".
    const projectTitle = [project.contract_number?.trim(), project.contract_name?.trim()].filter(Boolean).join(' - ');
    const salesPrice = Number(project.sales_price || 0);
    const totalCosts = Number(project.total_costs || 0);
    const profit = Number(project.profit || 0);
    const profitTone = profit >= 0 ? 'text-success-subtle-foreground' : 'text-destructive';

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start">
            {/* Main column — the commercial state and operational data at ~75%. */}
            <div className="space-y-4 lg:col-span-3">
                {/* Headline figures: what is this worth, what has it cost, where does it stand. */}
                <div className="bg-card grid grid-cols-1 divide-y overflow-hidden rounded-xl border sm:grid-cols-2 sm:divide-x xl:grid-cols-4 xl:divide-y-0">
                    <MetricTile
                        icon={<Tag className="h-5 w-5" />}
                        tone="bg-primary/12 text-primary"
                        label={t('projects.sidebar.sales_price', 'Sales price')}
                    >
                        {formatCurrency(salesPrice)}
                    </MetricTile>
                    {/* Plain text, not links — the summary card carries the navigation. */}
                    <MetricTile
                        icon={<Building2 className="h-5 w-5" />}
                        tone="bg-info-subtle text-info-subtle-foreground"
                        label={t('projects.sidebar.company', 'Company')}
                        valueClassName="text-base leading-snug"
                    >
                        {project.company ? (
                            project.company.name
                        ) : (
                            <span className="text-muted-foreground font-normal">{t('projects.sidebar.no_company', 'No company assigned')}</span>
                        )}
                    </MetricTile>
                    <MetricTile
                        icon={<UserRound className="h-5 w-5" />}
                        tone="bg-cat-purple text-cat-purple-foreground"
                        label={t('projects.sidebar.owner', 'Owner')}
                        valueClassName="text-base leading-snug"
                    >
                        {project.owner ? (
                            `${project.owner.first_name} ${project.owner.last_name}`
                        ) : (
                            <span className="text-muted-foreground font-normal">{t('projects.sidebar.no_owner', 'No owner assigned')}</span>
                        )}
                    </MetricTile>
                    {/* Status stays inline-editable, as it was before the redesign —
                        but only for holders of `projects.status.change`; everyone else
                        reads it. */}
                    <MetricTile
                        icon={<Activity className="h-5 w-5" />}
                        tone="bg-warning-subtle text-warning-subtle-foreground"
                        label={t('projects.sidebar.status', 'Status')}
                    >
                        <UnifiedStatusSelector
                            value={project.status}
                            statusType="project"
                            mode={canChangeStatus ? 'editable' : 'readonly'}
                            updateRoute="projects.update-status"
                            resourceId={project.id}
                        />
                    </MetricTile>
                </div>

                {/* The former Files tab — it owns the freed space in this column now. */}
                <ProjectFiles project={project} />
            </div>

            {/* Context column — orientation, counts and location at ~25%. */}
            <div className="space-y-4 lg:col-span-1">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>{t('projects.show.details.summary', 'Project summary')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <DetailField label={t('projects.show.details.project_name', 'Project name')} fallback={notSpecified}>
                            {projectTitle || null}
                        </DetailField>
                        <DetailField icon={<Link2 className="h-4 w-4" />} label={t('projects.sidebar.associated_lead', 'Associated Lead')}>
                            {project.lead ? (
                                <Link href={route('leads.show', project.lead.id)} className="text-primary hover:underline">
                                    {project.lead.lead_number}
                                </Link>
                            ) : (
                                <span className="text-muted-foreground font-normal">
                                    {t('projects.sidebar.no_associated_lead', 'No associated lead')}
                                </span>
                            )}
                        </DetailField>
                        {/*
                         * One calculation prices one project; picking another releases the
                         * old one. Carries the same Link2 icon as the associated lead above:
                         * both answer "what is this project attached to", and a calculator
                         * icon made the pair read as two unrelated things.
                         */}
                        <DetailField
                            icon={<Link2 className="h-4 w-4" />}
                            label={t('projects.show.details.calculation_request', 'Calculation request')}
                        >
                            <CalculationRequestSelect
                                value={linkedCalculationRequest?.id ?? null}
                                onValueChange={linkCalculationRequest}
                                requests={calculationRequestCandidates}
                                disabled={!canLinkCalculationRequest}
                                allowClear={false}
                                showNoneOption
                                className="-ml-2"
                                labels={{
                                    placeholder: t('projects.show.details.calculation_request_none', 'Not linked'),
                                    search: t('projects.show.details.calculation_request_search', 'Search calculations…'),
                                    empty: t('projects.show.details.calculation_request_empty', 'No calculation requests found.'),
                                    heading: t('projects.show.details.calculation_request', 'Calculation request'),
                                }}
                            />
                        </DetailField>

                        {/* Who the project belongs to — every entity stays clickable. */}
                        <div className="space-y-4 border-t pt-3">
                            <DetailField icon={<Building2 className="h-4 w-4" />} label={t('projects.sidebar.company', 'Company')}>
                                {project.company ? (
                                    <Link href={`/companies/${project.company.id}`} className="text-primary hover:underline">
                                        {project.company.name}
                                    </Link>
                                ) : (
                                    <span className="text-muted-foreground font-normal">
                                        {t('projects.sidebar.no_company', 'No company assigned')}
                                    </span>
                                )}
                            </DetailField>
                            <div className="space-y-4">
                                {/* Only this company's contacts are eligible — a contact from
                                    another company on this project means nothing. */}
                                <DetailField icon={<UserRound className="h-4 w-4" />} label={t('projects.sidebar.contact', 'Contact Person')}>
                                    <PersonSelect
                                        value={project.contact_id ?? null}
                                        onValueChange={updateContact}
                                        people={contactPeople}
                                        disabled={!canEditPeople}
                                        variant="inline"
                                        allowClear={false}
                                        showUnassignedOption
                                        showRole
                                        className="-ml-1.5"
                                        labels={{
                                            placeholder: t('projects.sidebar.no_contact', 'No contact assigned'),
                                            search: t('projects.sidebar.contact_search', 'Search contacts…'),
                                            empty: t('projects.sidebar.contact_empty', 'This company has no contacts yet.'),
                                            peopleHeading: t('projects.sidebar.contact_heading', 'Company contacts'),
                                            unassignedOption: t('projects.sidebar.no_contact', 'No contact assigned'),
                                        }}
                                    />
                                </DetailField>
                                {/* Anyone active can own a project; the picker just puts the
                                    commercially-responsible roles first. */}
                                <DetailField icon={<UserRound className="h-4 w-4" />} label={t('projects.sidebar.owner', 'Owner')}>
                                    <PersonSelect
                                        value={project.project_owner_id ?? null}
                                        onValueChange={updateOwner}
                                        people={ownerPeople}
                                        suggestedIds={ownerCandidates.suggested_ids}
                                        disabled={!canEditPeople}
                                        variant="inline"
                                        allowClear={false}
                                        showUnassignedOption
                                        showRole
                                        className="-ml-1.5"
                                        labels={{
                                            placeholder: t('projects.sidebar.no_owner', 'No owner assigned'),
                                            search: t('projects.sidebar.owner_search', 'Search colleagues…'),
                                            empty: t('projects.sidebar.owner_empty', 'No colleagues found.'),
                                            suggestedHeading: t('projects.sidebar.owner_suggested', 'Sales & management'),
                                            peopleHeading: t('projects.sidebar.owner_others', 'Other colleagues'),
                                            unassignedOption: t('projects.sidebar.no_owner', 'No owner assigned'),
                                        }}
                                    />
                                </DetailField>
                                {/* Closed list: only colleagues granted the Project Manager
                                    role can run a project, so the empty state names the role
                                    rather than pretending nobody was found. Assigning is
                                    narrower still — owner, creator or administrator only. */}
                                <DetailField
                                    icon={<ClipboardList className="h-4 w-4" />}
                                    label={t('projects.sidebar.project_manager', 'Project Manager')}
                                >
                                    <PersonSelect
                                        value={project.project_manager_id ?? null}
                                        onValueChange={updateProjectManager}
                                        people={projectManagerPeople}
                                        disabled={!canAssignProjectStaff}
                                        variant="inline"
                                        allowClear={false}
                                        showUnassignedOption
                                        showRole
                                        className="-ml-1.5"
                                        labels={{
                                            placeholder: t('projects.sidebar.no_project_manager', 'No project manager assigned'),
                                            search: t('projects.sidebar.project_manager_search', 'Search project managers…'),
                                            empty: t('projects.sidebar.project_manager_empty', 'No colleagues hold the Project Manager role.'),
                                            peopleHeading: t('projects.sidebar.project_manager_heading', 'Project managers'),
                                            unassignedOption: t('projects.sidebar.no_project_manager', 'No project manager assigned'),
                                        }}
                                    />
                                </DetailField>
                                {/* The engineering seat, closed to the Engineer role on the
                                    same terms as the project manager above. */}
                                <DetailField icon={<Wrench className="h-4 w-4" />} label={t('projects.sidebar.project_engineer', 'Engineer')}>
                                    <PersonSelect
                                        value={project.project_engineer_id ?? null}
                                        onValueChange={updateProjectEngineer}
                                        people={projectEngineerPeople}
                                        disabled={!canAssignProjectStaff}
                                        variant="inline"
                                        allowClear={false}
                                        showUnassignedOption
                                        showRole
                                        className="-ml-1.5"
                                        labels={{
                                            placeholder: t('projects.sidebar.no_project_engineer', 'No engineer assigned'),
                                            search: t('projects.sidebar.project_engineer_search', 'Search engineers…'),
                                            empty: t('projects.sidebar.project_engineer_empty', 'No colleagues hold the Engineer role.'),
                                            peopleHeading: t('projects.sidebar.project_engineer_heading', 'Engineers'),
                                            unassignedOption: t('projects.sidebar.no_project_engineer', 'No engineer assigned'),
                                        }}
                                    />
                                </DetailField>
                            </div>
                        </div>

                        {/* Contract timeline: the contract dates first, then the working dates. */}
                        <div className="space-y-4 border-t pt-3">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                                <DetailField label={t('projects.show.details.signature_date', 'Signature date')} fallback={notSpecified}>
                                    {project.signature_date ? formatDateForDisplay(String(project.signature_date).slice(0, 10)) : null}
                                </DetailField>
                                <DetailField label={t('projects.show.details.completion_date', 'Completion date')} fallback={notSpecified}>
                                    {project.completion_date ? formatDateForDisplay(String(project.completion_date).slice(0, 10)) : null}
                                </DetailField>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                                <DetailField label={t('projects.show.details.start_date', 'Start date')} fallback={notSpecified}>
                                    {project.start_date ? formatDateForDisplay(String(project.start_date).slice(0, 10)) : null}
                                </DetailField>
                                <DetailField label={t('projects.show.details.end_date', 'End date')} fallback={notSpecified}>
                                    {project.end_date ? formatDateForDisplay(String(project.end_date).slice(0, 10)) : null}
                                </DetailField>
                            </div>
                        </div>

                        {/* Financials: the headline figure first, then what it cost and what is left. */}
                        <div className="space-y-4 border-t pt-3">
                            <DetailField label={t('projects.sidebar.sales_price', 'Sales price')}>{formatCurrency(salesPrice)}</DetailField>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                                <DetailField label={t('projects.sidebar.total_costs', 'Total Costs')}>{formatCurrency(totalCosts)}</DetailField>
                                <DetailField label={t('projects.sidebar.profit', 'Profit')}>
                                    <span className={profitTone}>{formatCurrency(profit)}</span>
                                </DetailField>
                            </div>
                        </div>
                        {/* Record metadata closes out the card — supporting, not headline. */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-4 border-t pt-3">
                            <DetailField label={t('projects.show.details.created', 'Created')} fallback={notSpecified}>
                                {project.created_at ? formatDateTime(project.created_at) : null}
                            </DetailField>
                            <DetailField label={t('projects.show.details.updated', 'Updated')} fallback={notSpecified}>
                                {project.updated_at ? formatDateTime(project.updated_at) : null}
                            </DetailField>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>{t('projects.show.details.statistics', 'Statistics')}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <StatTile
                            icon={<ListChecks className="h-3.5 w-3.5" />}
                            label={t('projects.show.details.gantt_tasks', 'Gantt tasks')}
                            value={project.tasks?.length ?? 0}
                            onClick={onNavigateToTab ? () => onNavigateToTab('gantt') : undefined}
                        />
                        <StatTile
                            icon={<Euro className="h-3.5 w-3.5" />}
                            label={t('projects.show.details.cost_lines', 'Cost lines')}
                            value={project.cost_lines?.length ?? 0}
                            onClick={onNavigateToTab ? () => onNavigateToTab('costs') : undefined}
                        />
                        <StatTile
                            icon={<Link2 className="h-3.5 w-3.5" />}
                            label={t('projects.show.details.linked_purchase_orders', 'Linked purchase orders')}
                            value={project.purchase_orders?.length ?? 0}
                            onClick={onNavigateToTab ? () => onNavigateToTab('costs') : undefined}
                        />
                    </CardContent>
                </Card>

                {hasLocation && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle>{t('projects.show.details.location', 'Location')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-4 lg:grid-cols-1 xl:grid-cols-2">
                                <DetailField
                                    icon={<Globe className="h-4 w-4" />}
                                    label={t('projects.sidebar.country', 'Country')}
                                    fallback={t('projects.sidebar.no_country', 'No country provided')}
                                >
                                    {countryLabel}
                                </DetailField>
                                <DetailField
                                    icon={<MapPin className="h-4 w-4" />}
                                    label={t('projects.sidebar.address', 'Address')}
                                    fallback={notSpecified}
                                >
                                    {project.address_street ? String(project.address_street) : null}
                                </DetailField>
                                <DetailField icon={<House className="h-4 w-4" />} label={t('projects.sidebar.city', 'City')} fallback={notSpecified}>
                                    {project.address_city ? String(project.address_city) : null}
                                </DetailField>
                                <DetailField
                                    icon={<Mail className="h-4 w-4" />}
                                    label={t('projects.sidebar.postal_code', 'Postal Code')}
                                    fallback={notSpecified}
                                >
                                    {project.address_postal_code ? String(project.address_postal_code) : null}
                                </DetailField>
                            </div>
                            {/* Contextual, so the map stays a preview rather than a panel. */}
                            <AddressMapThumbnail
                                address={displayAddress}
                                lat={project.address_lat ?? undefined}
                                lng={project.address_lng ?? undefined}
                                heightClassName="h-48"
                            />
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
